import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, Image as ImageIcon, Volume2, Play, Pause, 
  ChevronDown, ChevronRight, Download, ExternalLink,
  BookOpen, Headphones, Film
} from 'lucide-react';
import { trackTutorialEvent } from '../../utils/tutorialAnalytics';

const VideoUputstvaView = ({ type = 'video' }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const pageViewOnce = useRef(false);

  useEffect(() => {
    if (pageViewOnce.current) return;
    pageViewOnce.current = true;
    trackTutorialEvent({
      event: 'page_view',
      source: 'dashboard_video',
      tutorialId: `page_video_uputstva_${type}`,
      kind: type,
      path: typeof window !== 'undefined' ? window.location.pathname : ''
    });
  }, [type]);

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const categories = [
    {
      id: 'uvod',
      title: 'Uvod u SpinTasker',
      description: 'Osnovno upoznavanje sa aplikacijom',
      icon: <BookOpen className="h-6 w-6" />,
      type: 'video',
      items: [
        {
          id: 'uvod-1',
          title: 'Dobrodošli u SpinTasker',
          description: 'Osnovni pregled aplikacije i njenih mogućnosti',
          duration: '3:24',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        },
        {
          id: 'uvod-2',
          title: 'Prvi koraci',
          description: 'Kako započeti rad sa aplikacijom',
          duration: '5:10',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        }
      ]
    },
    {
      id: 'dashboard',
      title: 'Dashboard i Navigacija',
      description: 'Upravljanje glavnom kontrolnom tablom',
      icon: <Film className="h-6 w-6" />,
      type: 'video',
      items: [
        {
          id: 'dashboard-1',
          title: 'Pregled Dashboard-a',
          description: 'Objašnjenje glavnih elemenata na dashboard-u',
          duration: '4:15',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        },
        {
          id: 'dashboard-2',
          title: 'Navigacija kroz aplikaciju',
          description: 'Kako se krećete kroz različite sekcije',
          duration: '3:45',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        }
      ]
    },
    {
      id: 'poslovi',
      title: 'Upravljanje Poslovima',
      description: 'Kreiranje, praćenje i upravljanje poslovima',
      icon: <Film className="h-6 w-6" />,
      type: 'video',
      items: [
        {
          id: 'poslovi-1',
          title: 'Kreiranje novog posla',
          description: 'Kako kreirati i dodeliti posao',
          duration: '6:30',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        },
        {
          id: 'poslovi-2',
          title: 'Praćenje statusa posla',
          description: 'Praćenje napretka i statusa poslova u realnom vremenu',
          duration: '5:20',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        },
        {
          id: 'poslovi-3',
          title: 'Kalendar i zakazivanje',
          description: 'Kako zakazati i organizovati poslove u kalendaru',
          duration: '7:10',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        }
      ]
    },
    {
      id: 'klijenti',
      title: 'Upravljanje Klijentima',
      description: 'Dodavanje, pregled i upravljanje klijentima',
      icon: <Film className="h-6 w-6" />,
      type: 'video',
      items: [
        {
          id: 'klijenti-1',
          title: 'Dodavanje novog klijenta',
          description: 'Kako dodati novog klijenta u sistem',
          duration: '4:00',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        },
        {
          id: 'klijenti-2',
          title: 'Istorija poslova klijenta',
          description: 'Pregled svih poslova za određenog klijenta',
          duration: '3:30',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        }
      ]
    },
    {
      id: 'radnici',
      title: 'Upravljanje Radnicima',
      description: 'Dodavanje radnika i dodeljivanje poslova',
      icon: <Film className="h-6 w-6" />,
      type: 'video',
      items: [
        {
          id: 'radnici-1',
          title: 'Dodavanje radnika',
          description: 'Kako dodati novog radnika u tim',
          duration: '5:45',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        },
        {
          id: 'radnici-2',
          title: 'Dodeljivanje poslova radnicima',
          description: 'Kako dodeliti poslove određenim radnicima',
          duration: '4:20',
          url: '#',
          thumbnail: '/api/placeholder/400/225'
        }
      ]
    },
    {
      id: 'slike',
      title: 'Slikovita Objašnjenja',
      description: 'Galerija slika sa objašnjenjima',
      icon: <ImageIcon className="h-6 w-6" />,
      type: 'image',
      items: [
        {
          id: 'slika-1',
          title: 'Dashboard pregled',
          description: 'Detaljni pregled elemenata dashboard-a',
          url: '/api/placeholder/800/600',
          thumbnail: '/api/placeholder/400/300'
        },
        {
          id: 'slika-2',
          title: 'Forma za kreiranje posla',
          description: 'Svi koraci kreiranja novog posla',
          url: '/api/placeholder/800/600',
          thumbnail: '/api/placeholder/400/300'
        },
        {
          id: 'slika-3',
          title: 'Kalendar prikaz',
          description: 'Kako koristiti kalendar za zakazivanje',
          url: '/api/placeholder/800/600',
          thumbnail: '/api/placeholder/400/300'
        }
      ]
    },
    {
      id: 'audio',
      title: 'Audio Objašnjenja',
      description: 'Audio zapisi sa detaljnim objašnjenjima',
      icon: <Headphones className="h-6 w-6" />,
      type: 'audio',
      items: [
        {
          id: 'audio-1',
          title: 'Uvodni audio vodič',
          description: 'Audio objašnjenje osnovnih funkcija',
          duration: '8:15',
          url: '#'
        },
        {
          id: 'audio-2',
          title: 'Kreiranje i upravljanje poslovima',
          description: 'Audio vodič kroz proces upravljanja poslovima',
          duration: '12:30',
          url: '#'
        },
        {
          id: 'audio-3',
          title: 'Napredne funkcije',
          description: 'Audio objašnjenje naprednih opcija',
          duration: '15:45',
          url: '#'
        }
      ]
    }
  ];

  // Filter categories based on type prop
  const filteredCategories = categories.filter(category => category.type === type);

  const handlePlayVideo = (categoryId, item) => {
    if (!item?.id) return;
    if (playingVideo === item.id) {
      setPlayingVideo(null);
    } else {
      trackTutorialEvent({
        event: 'play',
        source: 'dashboard_video',
        tutorialId: `${categoryId}/${item.id}`,
        title: item.title || '',
        kind: type
      });
      setPlayingVideo(item.id);
    }
  };

  const getTypeConfig = () => {
    if (type === 'image') {
      return {
        title: 'Slikovita Uputstva',
        description: 'Galerija slika sa detaljnim objašnjenjima rada aplikacije',
        icon: <ImageIcon className="h-8 w-8 text-white" />,
        gradient: 'from-blue-500 to-cyan-500',
        bannerText: 'Slikoviti materijali će biti dostupni nakon što ih admin unese u sistem.'
      };
    }
    return {
      title: 'Video Uputstva',
      description: 'Video snimci sa objašnjenjima rada aplikacije',
      icon: <Video className="h-8 w-8 text-white" />,
      gradient: 'from-purple-500 to-blue-500',
      bannerText: 'Video materijali će biti dostupni nakon što ih admin unese u sistem.'
    };
  };

  const typeConfig = getTypeConfig();

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`bg-gradient-to-br ${typeConfig.gradient} rounded-lg p-3`}>
            {typeConfig.icon}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {typeConfig.title}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {typeConfig.description}
            </p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Napomena:</strong> {typeConfig.bannerText}
        </p>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400">
              Trenutno nema dostupnih {type === 'image' ? 'slikovitih' : 'video'} uputstava.
            </p>
          </div>
        ) : (
          filteredCategories.map((category) => (
          <div
            key={category.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-3">
                  <div className="text-purple-600 dark:text-purple-400">
                    {category.icon}
                  </div>
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                    {category.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {category.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                  {category.items.length} {category.type === 'video' ? 'video' : category.type === 'audio' ? 'audio' : 'slika'}
                </span>
                {expandedCategory === category.id ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {expandedCategory === category.id && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900/50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.items.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      {/* Video/Image/Audio Preview */}
                      <div className="relative bg-gray-100 dark:bg-gray-700 aspect-video">
                        {category.type === 'video' && (
                          <>
                            {playingVideo === item.id ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-black">
                                <video
                                  controls
                                  className="w-full h-full"
                                  src={item.url}
                                >
                                  Vaš pretraživač ne podržava video tag.
                                </video>
                                <button
                                  onClick={() => setPlayingVideo(null)}
                                  className="absolute top-2 right-2 bg-black/50 text-white p-2 rounded hover:bg-black/70"
                                >
                                  <Pause className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <img
                                  src={item.thumbnail || '/api/placeholder/400/225'}
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="225"%3E%3Crect fill="%23ddd" width="400" height="225"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EVideo thumbnail%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                                  <button
                                    onClick={() => handlePlayVideo(category.id, item)}
                                    className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-4 transition-transform hover:scale-110"
                                  >
                                    <Play className="h-8 w-8 fill-current" />
                                  </button>
                                </div>
                                {item.duration && (
                                  <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                    {item.duration}
                                  </span>
                                )}
                              </>
                            )}
                          </>
                        )}
                        
                        {category.type === 'image' && (
                          <>
                            <img
                              src={item.thumbnail || item.url}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors" />
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white p-2 rounded"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </>
                        )}
                        
                        {category.type === 'audio' && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-500">
                            <div className="text-center text-white">
                              <Volume2 className="h-12 w-12 mx-auto mb-2" />
                              {item.duration && (
                                <span className="text-sm">{item.duration}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {item.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => handlePlayVideo(category.id, item)}
                            className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center space-x-1"
                          >
                            <Play className="h-4 w-4" />
                            <span>
                              {category.type === 'video' ? 'Reproduciraj' : 
                               category.type === 'audio' ? 'Slušaj' : 
                               'Pogledaj'}
                            </span>
                          </button>
                          <button className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center space-x-1">
                            <Download className="h-4 w-4" />
                            <span>Preuzmi</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          ))
        )}
      </div>

      {/* Quick Stats */}
      {type === 'video' && (
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Video className="h-8 w-8" />
            <span className="text-3xl font-bold">
              {filteredCategories.reduce((acc, c) => acc + c.items.length, 0)}
            </span>
          </div>
          <p className="text-sm opacity-90">Ukupno Video Uputstava</p>
        </div>
      )}
      
      {type === 'image' && (
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <ImageIcon className="h-8 w-8" />
            <span className="text-3xl font-bold">
              {filteredCategories.reduce((acc, c) => acc + c.items.length, 0)}
            </span>
          </div>
          <p className="text-sm opacity-90">Ukupno Slikovitih Uputstava</p>
        </div>
      )}
    </div>
  );
};

export default VideoUputstvaView;

